type ScheduleValue = boolean | number | string | null | undefined;

export type AllowedDays = [boolean, boolean, boolean, boolean, boolean, boolean, boolean];

export interface ScheduleSource {
  leadTime?: ScheduleValue;
  LeadTime?: ScheduleValue;
  isSunday?: ScheduleValue;
  IsSunday?: ScheduleValue;
  isMonday?: ScheduleValue;
  IsMonday?: ScheduleValue;
  isTuesday?: ScheduleValue;
  IsTuesday?: ScheduleValue;
  isWednesday?: ScheduleValue;
  IsWednesday?: ScheduleValue;
  isThursday?: ScheduleValue;
  IsThursday?: ScheduleValue;
  isFriday?: ScheduleValue;
  IsFriday?: ScheduleValue;
  isSaturday?: ScheduleValue;
  IsSaturday?: ScheduleValue;
}

export interface NormalizedSchedule {
  leadTime?: number;
  isSunday?: boolean;
  isMonday?: boolean;
  isTuesday?: boolean;
  isWednesday?: boolean;
  isThursday?: boolean;
  isFriday?: boolean;
  isSaturday?: boolean;
}

export interface ScheduleDiagnostics {
  rawScheduleKeys: string[];
  hasAnyWorkDaySchedule: boolean;
  hasSaturdayValue: boolean;
  hasSundayValue: boolean;
  missingWeekendValues: Array<'isSaturday' | 'isSunday'>;
  normalized: NormalizedSchedule;
  allowedDays: AllowedDays;
}

const hasValue = (value: ScheduleValue) => value !== undefined && value !== null;

const toBoolean = (value: ScheduleValue, fallback: boolean): boolean => {
  if (!hasValue(value)) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;

  const normalized = value.trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return fallback;
};

const toNumber = (value: ScheduleValue): number | undefined => {
  if (!hasValue(value) || typeof value === 'boolean') return undefined;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : undefined;
};

const getRaw = (
  source: ScheduleSource | null | undefined,
  camelKey: keyof ScheduleSource,
  pascalKey: keyof ScheduleSource
): ScheduleValue => source?.[camelKey] ?? source?.[pascalKey];

const WORK_DAY_KEYS: Array<keyof ScheduleSource> = [
  'isSunday',
  'IsSunday',
  'isMonday',
  'IsMonday',
  'isTuesday',
  'IsTuesday',
  'isWednesday',
  'IsWednesday',
  'isThursday',
  'IsThursday',
  'isFriday',
  'IsFriday',
  'isSaturday',
  'IsSaturday',
];

const SCHEDULE_KEYS: Array<keyof ScheduleSource> = [
  'leadTime',
  'LeadTime',
  ...WORK_DAY_KEYS,
];

const hasOwn = (source: ScheduleSource | null | undefined, key: keyof ScheduleSource): boolean =>
  !!source && Object.prototype.hasOwnProperty.call(source, key);

export function normalizeSchedule(source: ScheduleSource | null | undefined): NormalizedSchedule {
  const leadTime = toNumber(getRaw(source, 'leadTime', 'LeadTime'));
  const schedule: NormalizedSchedule = {
    ...(leadTime !== undefined ? { leadTime } : {}),
  };

  if (!hasWorkDaySchedule(source)) {
    return schedule;
  }

  return {
    ...schedule,
    isSunday: toBoolean(getRaw(source, 'isSunday', 'IsSunday'), false),
    isMonday: toBoolean(getRaw(source, 'isMonday', 'IsMonday'), true),
    isTuesday: toBoolean(getRaw(source, 'isTuesday', 'IsTuesday'), true),
    isWednesday: toBoolean(getRaw(source, 'isWednesday', 'IsWednesday'), true),
    isThursday: toBoolean(getRaw(source, 'isThursday', 'IsThursday'), true),
    isFriday: toBoolean(getRaw(source, 'isFriday', 'IsFriday'), true),
    isSaturday: toBoolean(getRaw(source, 'isSaturday', 'IsSaturday'), false),
  };
}

export function hasWorkDaySchedule(source: ScheduleSource | null | undefined): boolean {
  if (!source) return false;
  return WORK_DAY_KEYS.some((key) => hasValue(source[key]));
}

export function getAllowedDays(source: ScheduleSource | null | undefined): AllowedDays {
  if (!hasWorkDaySchedule(source)) {
    return [true, true, true, true, true, true, true];
  }

  const schedule = normalizeSchedule(source);
  return [
    schedule.isSunday ?? false,
    schedule.isMonday ?? true,
    schedule.isTuesday ?? true,
    schedule.isWednesday ?? true,
    schedule.isThursday ?? true,
    schedule.isFriday ?? true,
    schedule.isSaturday ?? false,
  ];
}

export function getScheduleDiagnostics(source: ScheduleSource | null | undefined): ScheduleDiagnostics {
  const hasSaturdayValue = hasValue(getRaw(source, 'isSaturday', 'IsSaturday'));
  const hasSundayValue = hasValue(getRaw(source, 'isSunday', 'IsSunday'));
  const missingWeekendValues: Array<'isSaturday' | 'isSunday'> = [];

  if (!hasSaturdayValue) {
    missingWeekendValues.push('isSaturday');
  }
  if (!hasSundayValue) {
    missingWeekendValues.push('isSunday');
  }

  return {
    rawScheduleKeys: SCHEDULE_KEYS.filter((key) => hasOwn(source, key)),
    hasAnyWorkDaySchedule: hasWorkDaySchedule(source),
    hasSaturdayValue,
    hasSundayValue,
    missingWeekendValues,
    normalized: normalizeSchedule(source),
    allowedDays: getAllowedDays(source),
  };
}

export function hasWeekendScheduleValues(source: ScheduleSource | null | undefined): boolean {
  const diagnostics = getScheduleDiagnostics(source);
  return diagnostics.hasSaturdayValue && diagnostics.hasSundayValue;
}

export function getLeadTime(source: ScheduleSource | null | undefined, fallback = 1): number {
  return normalizeSchedule(source).leadTime ?? fallback;
}

export function toDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseDateString(value: string): Date {
  return new Date(value + 'T00:00:00');
}

export function isSelectableServiceDate(
  date: Date,
  allowedDays: AllowedDays,
  holidaySet: Set<string>
): boolean {
  return allowedDays[date.getDay()] && !holidaySet.has(toDateString(date));
}

export function getEarliestServiceDate(
  leadTime: number,
  allowedDays: AllowedDays,
  holidaySet: Set<string>
): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);

  if (!allowedDays.some(Boolean)) {
    return toDateString(date);
  }

  let remainingDays = Math.max(0, leadTime);
  while (remainingDays > 0) {
    date.setDate(date.getDate() + 1);
    if (isSelectableServiceDate(date, allowedDays, holidaySet)) {
      remainingDays -= 1;
    }
  }

  while (!isSelectableServiceDate(date, allowedDays, holidaySet)) {
    date.setDate(date.getDate() + 1);
  }

  return toDateString(date);
}

export function isServiceDateValid(
  dateString: string,
  minDate: string,
  allowedDays: AllowedDays,
  holidaySet: Set<string>
): boolean {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return false;

  const date = parseDateString(dateString);
  const min = parseDateString(minDate);
  return date >= min && isSelectableServiceDate(date, allowedDays, holidaySet);
}
